import "../css/home.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { AiFillInstagram, AiFillTikTok } from "react-icons/ai";
import { FiArrowRight } from "react-icons/fi"; // Added a sleek arrow icon
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import npors from "../images/npors-logo.jpg";
import nvq from "../images/nvq.jpg";
import cpcs from "../images/cpcs-logo.jpg";
import bg from "../images/constructionbackground.jpg";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { useState, useEffect, useRef } from "react";

export default function Home() {
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackClass, setFeedbackClass] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollRef = useRef();

  const whoWeAreCards = [
    {
      title: "5+ years in business",
      content:
        "Our certified instructors bring years of real-world experience to every session.",
    },
    {
      title: "State-of-the-Art Equipment",
      content:
        "Train using the latest industry-standard tools and technology for a truly hands-on experience.",
    },
    {
      title: "Personalized Training",
      content:
        "Courses designed and tailored to align with your professional objectives and career aspirations.",
    },
    {
      title: "Recognized Credentials",
      content:
        "Earn certifications that are respected and valued across the UK construction and trade sectors.",
    },
    {
      title: "Flexible Learning",
      content:
        "Choose from weekend or evening classes to balance your training with your personal commitments.",
    },
  ];

  useEffect(() => {
    const container = scrollRef.current;
    let isDown = false;
    let startX;
    let scrollLeft;
    let animationFrame;

    const handleMouseDown = (e) => {
      isDown = true;
      container.classList.add("active");
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      container.classList.remove("active");
      cancelAnimationFrame(animationFrame);
    };

    const handleMouseUp = () => {
      isDown = false;
      container.classList.remove("active");
      cancelAnimationFrame(animationFrame);
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        container.scrollLeft = scrollLeft - walk;
      });
    };

    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("mouseleave", handleMouseLeave);
    container.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mousemove", handleMouseMove);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = {
      name: e.target.name.value,
      email: e.target.email.value,
      phone: e.target.phone.value,
      message: e.target.message.value,
    };

    try {
      const response = await fetch(
        "https://www.alphacontraining.co.uk/sendcontactemail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        },
      );

      const result = await response.json();
      if (response.ok) {
        setFeedbackMessage("Your message has been sent successfully.");
        setFeedbackClass("success");
      } else {
        setFeedbackMessage(`Failed to send message: ${result.error}`);
        setFeedbackClass("error");
        setIsSubmitting(false);
      }
    } catch (error) {
      setFeedbackMessage("There was an error. Please try again.");
      setFeedbackClass("error");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App">
      <Helmet>
        <title>Home | Alpha Construction Training</title>
        <meta
          name="description"
          content="Alpha Constructions offers premium CPCS, NPORS & NVQ training to advance your career."
        />
        <link rel="canonical" href="https://www.alphacontraining.co.uk" />
      </Helmet>

      <Footer />
      <Navbar />

      {/* Premium Hero Section */}
      <div className="hero">
        <div className="hero-bg" style={{ backgroundImage: `url(${bg})` }} />
        <div className="hero-overlay"></div>

        <div className="hero-content-wrapper">
          <div className="hero-content">
            {/* 2. Main Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              Alpha Construction <br /> Training.
            </motion.h1>

            {/* 3. Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.2,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              Build Skills. Build Futures. Elevate your career with
              industry-leading machinery and safety certifications.
            </motion.p>

            {/* 4. CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.8,
                delay: 0.3,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <a href="#courses" className="btn-primary">
                Explore Courses <FiArrowRight />
              </a>
            </motion.div>
          </div>

          <motion.div
            className="hero-floating-stats"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="stat-item">
              <span className="stat-number">98%</span>
              <span className="stat-label">First-Time Pass Rate</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">5+</span>
              <span className="stat-label">Years of Excellence</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">5000+</span>
              <span className="stat-label">Operators Certified</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Who We Are Section */}
      <div className="who-we-are-wrapper">
        <div className="section-header">
          <h2>The Alpha Standard</h2>
          <p>Why industry professionals choose us for their accreditation.</p>
        </div>

        <div className="who-we-are-scroll" ref={scrollRef}>
          {whoWeAreCards.map((card, index) => (
            <div key={index} className="who-we-are-card">
              <div className="card-number">0{index + 1}</div>
              <h3>{card.title}</h3>
              <p>{card.content}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Courses Section */}
      <div id="courses" className="cards-wrapper">
        <div className="section-header">
          <h2>Our Credentials</h2>
          <p>
            Comprehensive training pathways for the modern construction site.
          </p>
        </div>
        <div className="cards-container">
          <div className="cards">
            {[
              {
                title: "CPCS",
                img: cpcs,
                link: "/cpcs",
                desc: "Essential certifications for plant machinery operators, ensuring site safety.",
              },
              {
                title: "NPORS",
                img: npors,
                link: "/npors",
                desc: "Flexible, high-quality alternative for machinery operation accreditation.",
              },
              {
                title: "NVQ",
                img: nvq,
                link: "/nvq",
                desc: "On-the-job skill recognition qualifying professionals for career advancement.",
              },
            ].map((course, idx) => (
              <a href={course.link} className="course-card" key={idx}>
                <div className="course-img-wrapper">
                  <img src={course.img} alt={`${course.title} card`} />
                </div>
                <div className="course-info">
                  <h2>{course.title}</h2>
                  <p>{course.desc}</p>
                  <span className="course-link">
                    View Details <FiArrowRight />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="testimonials-section">
        <div className="section-header">
          <h2>Client Success</h2>
        </div>
        <div className="testimonials-grid">
          {[
            {
              name: "Lak S.",
              review:
                "Excellent communication skills with clients and they make the training so easy. Highly recommend for any plant training.",
              stars: 5,
            },
            {
              name: "Jovanni B.",
              review:
                "I must say I’m very pleased with the customer services and the knowledge I’ve gained. I’ll definitely recommend them for more courses!",
              stars: 5,
            },
            {
              name: "Collins S.",
              review:
                "I did my traffic Marshall CPCS with Raj and I am very happy with the experience. I will be coming back to do my NVQ.",
              stars: 5,
            },
          ].map((t, i) => (
            <div key={i} className="premium-review-card">
              <div className="stars">
                {Array.from({ length: t.stars }, (_, idx) => (
                  <i key={idx} className="fas fa-star"></i>
                ))}
              </div>
              <p className="review-text">"{t.review}"</p>
              <p className="review-name">— {t.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact Section */}
      <div className="contact-section">
        <div className="contact-grid">
          <div className="contact-info-panel">
            <h2>
              Let's build
              <br />
              your future.
            </h2>
            <p className="contact-sub">
              Reach out to our team to schedule your next accreditation.
            </p>

            <div className="info-details">
              <p>
                <strong>Email:</strong> info@alphacontraining.co.uk
              </p>
              <p>
                <strong>Phone:</strong> 07475510591
              </p>
            </div>

            <div className="social-links">
              <a
                href="https://www.tiktok.com/@alpha.con.training"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AiFillTikTok />
              </a>
              <a
                href="https://www.instagram.com/alpha.con.training"
                target="_blank"
                rel="noopener noreferrer"
              >
                <AiFillInstagram />
              </a>
            </div>
          </div>

          <div className="contact-form-panel">
            <form onSubmit={handleSubmit} className="premium-form">
              {feedbackMessage && (
                <p className={`feedback-message ${feedbackClass}`}>
                  {feedbackMessage}
                </p>
              )}

              <div className="input-group">
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Full Name"
                  required
                />
              </div>
              <div className="input-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email Address"
                  required
                />
              </div>
              <div className="input-group">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Phone Number"
                  required
                />
              </div>
              <div className="input-group">
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  placeholder="How can we help you?"
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send Inquiry"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
